import CloseIcon from "@mui/icons-material/Close";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Snackbar, { type SnackbarCloseReason } from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import React from "react";
import { type Snackbar as QueuedSnackbar, snackbarQueue } from "_shared/_providers/snackbars/snackbars";
import { isSignOutInProgress } from "users/__session/sign-out-in-progress";

/**
 * Whether a toast says nothing because the document is signing out.
 *
 * A failure raised after sign-out began describes the session that just ended, not anything the
 * person can act on: a refetch that went out with no token, or a page that re-rendered over a session
 * with no user. The login page is already loading, so the message would only flash red over a page
 * that is leaving (#5827). Gating here, where every toast is drawn, covers every call site and both
 * ways of reaching it — `useSnackbar()` and the module-level `enqueueSnackbar` — without any of them
 * knowing that sign-out exists.
 *
 * Sign-out makes no request of its own today, so no failure here can be the reason a sign-out did not
 * finish. A sign-out that gains one must report its failure some other way, or it will be silent too.
 */
function isSilencedBySignOut(snackbar: QueuedSnackbar): boolean {
  return (snackbar.variant === "error" || snackbar.variant === "warning") && isSignOutInProgress();
}

function AppSnackbar(props: { readonly snackbar: QueuedSnackbar }) {
  const { snackbar } = props;
  const openedOnMount = React.useRef(snackbar.open);

  // A toast asked to close before it was ever drawn — the oldest of four raised in one go — never
  // opens, so MUI never runs its exit and never calls `onExited`. Without this it would hold its slot
  // for good and nothing behind it would ever show.
  React.useEffect(() => {
    if (!openedOnMount.current && !snackbar.open) snackbarQueue.remove(snackbar.key);
  }, [snackbar.key, snackbar.open]);

  // Only its own timer or its close button closes a toast. A click elsewhere on the page, or Escape
  // pressed to leave a dialog, is not a request to close every toast on screen — notistack ignored
  // both, and a persisting warning must survive them.
  const handleClose = (_event: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason !== "timeout") return;
    snackbarQueue.close(snackbar.key);
  };

  return (
    <Snackbar
      open={snackbar.open}
      autoHideDuration={snackbar.autoHideDuration}
      onClose={handleClose}
      {...snackbar.snackbarProps}
      slotProps={{ transition: { onExited: () => snackbarQueue.remove(snackbar.key) } }}
      // The stack below positions the toasts; each one sits in it rather than at a fixed corner.
      sx={{ position: "static" }}
    >
      {isSilencedBySignOut(snackbar) ? (
        <div hidden data-testid="snackbar-silenced-by-sign-out" />
      ) : (
        <Alert
          data-testid="app-snackbar"
          data-variant={snackbar.variant}
          severity={snackbar.variant}
          role={snackbar.role ?? "alert"}
          variant="filled"
          onClose={() => snackbarQueue.close(snackbar.key)}
          // An action replaces Alert's own close button, so the close button is drawn after it.
          action={
            snackbar.action && (
              <>
                {snackbar.action(snackbar.key)}
                <IconButton
                  aria-label="Close"
                  color="inherit"
                  size="small"
                  onClick={() => snackbarQueue.close(snackbar.key)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            )
          }
          sx={{ width: { xs: "100%", sm: "auto" }, minWidth: { sm: 288 }, maxWidth: { sm: 560 }, boxShadow: 6 }}
        >
          {snackbar.message}
        </Alert>
      )}
    </Snackbar>
  );
}

/** Draws the app's toasts in the bottom-left corner, newest on top, at most three at once. */
export default function AppSnackbarProvider(props: { readonly children: React.ReactNode }) {
  const snackbars = React.useSyncExternalStore(snackbarQueue.subscribe, snackbarQueue.getVisible);

  return (
    <>
      {props.children}
      {snackbars.length > 0 && (
        <Stack
          data-testid="app-snackbars"
          direction="column-reverse"
          spacing={1}
          sx={{
            position: "fixed",
            zIndex: (theme) => theme.zIndex.snackbar,
            bottom: { xs: 8, sm: 24 },
            left: { xs: 8, sm: 24 },
            right: { xs: 8, sm: "auto" },
            alignItems: { xs: "stretch", sm: "flex-start" },
            pointerEvents: "none",
            "& > *": { pointerEvents: "auto" },
          }}
        >
          {snackbars.map((snackbar) => (
            <AppSnackbar key={snackbar.key} snackbar={snackbar} />
          ))}
        </Stack>
      )}
    </>
  );
}
